import { useAtom } from 'jotai'
import { ChangeEvent, useState } from 'react'

import useCustomSearch from '@/hooks/useCustomSearch'
import { approvalsAtom, approvalsTotalAtom, compliancesAtom, compliancesTotalAtom } from '@/store/tasksApprovals'
import { IApproval, ICompliance, TIntegratedSystems, TType } from '@/types/tasksApprovals'
import { deepClone } from '@/utils'
import filterBySearch from '@/utils/filterBySearch'

import ItemsTabs from './Tabs'

const Container = () => {
    const [tasks] = useAtom(compliancesAtom)
    const [approvals] = useAtom(approvalsAtom)
    const [tasksTotal] = useAtom(compliancesTotalAtom)
    const [approvalsTotal] = useAtom(approvalsTotalAtom)
    const allTasks = deepClone<ICompliance[]>(tasks)
    const allApprovals = deepClone<IApproval[]>(approvals)
    const [searchValue, setSearchValue] = useState('')

    const { filters, updateType } = useCustomSearch('/tasks-approvals')
    const { type, services } = filters

    const filterAll = (tasks: ICompliance[], approvals: IApproval[], filters: TIntegratedSystems[]) => {
        if (!filters.length)
            return {
                tasks: tasks,
                approvals: approvals,
            }

        const taskSystems = [...new Set(tasks.map((task) => task.system_id))]
        const approvalsSystems = [...new Set(approvals.map((approval) => approval.system_id))]

        const tasksFilters = filters.filter((filter) => {
            if (filter === 'MY_LEARNING') {
                return taskSystems.find((system) => system.includes(filter))
            } else return taskSystems.includes(filter)
        })

        const approvalsFilters = filters.filter((filter) => approvalsSystems.includes(filter))

        let filteredTasks: ICompliance[] = []
        let filteredApprovals: IApproval[] = []

        if (tasksFilters.length) {
            filteredTasks = filterTasks(tasks, tasksFilters)
        }

        if (approvalsFilters.length) {
            filteredApprovals = filterApprovals(approvals, approvalsFilters)
        }

        return {
            tasks: filteredTasks,
            approvals: filteredApprovals,
        }
    }

    const filterTasks = (tasks: ICompliance[], filters: string[]) => {
        if (!filters.length) return tasks

        let filteredTasks: ICompliance[] = []

        for (const filter of filters) {
            const results = tasks.filter((task) => task.system_id.includes(filter))
            if (results.length) filteredTasks = [...filteredTasks, ...results]
        }

        return filteredTasks
    }

    const filterApprovals = (approvals: IApproval[], filters: TIntegratedSystems[]) => {
        if (!filters.length) return approvals

        let filteredApprovals: IApproval[] = []

        for (const filter of filters) {
            const results = approvals.filter((task) => task.system_id === filter)
            if (results.length) filteredApprovals = [...filteredApprovals, ...results]
        }

        return filteredApprovals
    }

    const handleTabSwitch = (id: TType) => {
        updateType(id)
        if (searchValue) setSearchValue('')
    }

    const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value)
    }

    const filteredAll = filterAll(
        filterBySearch(allTasks, ['description', 'title'], searchValue),
        filterBySearch(allApprovals, ['description', 'requester_name'], searchValue),
        services.all,
    )
    const filteredTasks: ICompliance[] = filterTasks(filterBySearch(tasks, ['description', 'title'], searchValue), services.tasks)
    const filteredApprovals: IApproval[] = filterApprovals(
        filterBySearch(approvals, ['description', 'requester_name'], searchValue),
        services.approvals,
    )

    const tabsMap = {
        all: 0,
        tasks: 1,
        approvals: 2,
    }

    const tabs: Map<string, number> = new Map(Object.entries(tabsMap))
    const activeTab = tabs.get(type) ?? 0

    return (
        <ItemsTabs
            handleTabSwitch={handleTabSwitch}
            all={filteredAll}
            approvals={filteredApprovals}
            tasks={filteredTasks}
            activeTab={activeTab}
            notifications={{ tasks: tasksTotal, approvals: approvalsTotal }}
            onSearch={onSearch}
            searchValue={searchValue}
        />
    )
}

export default Container
